import type { ReactNode } from 'react';
import { cx } from '../cx';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'E';

/** Circle sizes: 24px (an info chip) or 40px (a standalone mark). Inside the labelled pill the circle is 20px. */
const SIZE = { sm: 'size-6 text-xs', md: 'size-6 text-xs', lg: 'size-10 text-lg' } as const;

export type GradeBadgeProps = {
  /** The letter, already mapped from the score by the caller. Null (no grade yet) renders nothing. */
  grade: Grade | null | undefined;
  size?: keyof typeof SIZE;
  /** Visible text beside the letter, e.g. "Employer Score". Turns the badge into a pill. */
  label?: ReactNode;
  /** Name for screen readers when there is no visible label, e.g. "Employer Score B". */
  accessibleLabel?: string;
  /** On an employer brand surface: a white circle with ink letter, so it reads on any band. */
  onBrand?: boolean;
  /** Links the badge to the methodology. */
  href?: string;
  className?: string;
};

/**
 * The Employer Score as one letter, A to E; never the 0 to 100 number, and no per-grade colour
 * scale: the letter carries the meaning.
 */
export function GradeBadge({ grade, size = 'md', label, accessibleLabel, onBrand, href, className }: GradeBadgeProps) {
  if (!grade) return null;
  const circle = (
    <span
      aria-hidden={accessibleLabel && !label ? true : undefined}
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-full font-display font-bold leading-none',
        label ? 'size-5 text-xs' : SIZE[size],
        // On a brand band everything sits on white logo surfaces, whose ink never flips with the theme.
        onBrand
          ? label
            ? 'bg-jc-on-logo-surface text-jc-logo-surface'
            : 'bg-jc-logo-surface text-jc-on-logo-surface'
          : 'bg-jc-ink text-jc-surface',
      )}
    >
      {grade}
    </span>
  );
  const content = label ? (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1.5 rounded-jc-info pr-2.5 pl-0.5 text-xs font-semibold',
        onBrand
          ? 'bg-jc-logo-surface text-jc-on-logo-surface'
          : 'bg-jc-surface text-jc-ink ring-1 ring-inset ring-jc-border-control',
      )}
    >
      {circle}
      {label}
    </span>
  ) : (
    circle
  );
  const name = accessibleLabel && !label ? accessibleLabel : undefined;
  if (href) {
    return (
      <a
        href={href}
        aria-label={name}
        className={cx(
          label ? 'inline-flex rounded-jc-info' : 'inline-flex rounded-full',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
          className,
        )}
      >
        {content}
      </a>
    );
  }
  return (
    <span role={name ? 'img' : undefined} aria-label={name} className={cx('inline-flex', className)}>
      {content}
    </span>
  );
}
